<script lang="ts">
	import classNames from 'classnames';
	import { onMount } from 'svelte';
	import { swipe, type SwipeCustomEvent } from 'svelte-gestures';
	import { twMerge } from 'tailwind-merge';
	import { ChevronLeftIcon } from '../../assets/icons/ChevronLeftIcon';
	import { ChevronRightIcon } from '../../assets/icons/ChevronRightIcon';
	import { CrossIcon } from '../../assets/icons/CrossIcon';
	import { Button } from '../../components/Button';
	import { Dots } from '../Dots';
	import { VideoPlayer } from '../VideoPlayer';
	import { LightboxImage } from './LightboxImage';
	import { type GalleryItem, GalleryMedia } from './types';

	interface Props {
		onClose: () => void;
		onClickLeftArrow?: (() => void) | undefined;
		onClickRightArrow?: (() => void) | undefined;
		onDotClick?: ((index: number) => void) | undefined;
		dataCy: string;
		selectedItem: GalleryItem | undefined;
		selectedItemIndex: number;
		itemsLength: number;
	}

	let {
		onClose,
		onClickLeftArrow = undefined,
		onClickRightArrow = undefined,
		onDotClick = undefined,
		dataCy,
		selectedItem,
		selectedItemIndex,
		itemsLength
	}: Props = $props();

	onMount(() => {
		document.body.style.overflow = 'hidden';

		const handleKeyDown = (e: { key: string }) => {
			switch (e.key) {
				case 'Escape': {
					onClose();
					return;
				}
				case 'ArrowRight': {
					if (onClickRightArrow) {
						onClickRightArrow();
					}

					return;
				}
				case 'ArrowLeft': {
					if (onClickLeftArrow) {
						onClickLeftArrow();
					}

					return;
				}
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.body.style.overflow = 'visible';
		};
	});

	let noControls = $derived(itemsLength === 1);
	let dataCyPrefix = $derived(`${dataCy}-lightbox`);

	const handleSwipe = (event: SwipeCustomEvent) => {
		if (event.detail.direction === 'left' && onClickRightArrow) {
			onClickRightArrow();
		} else if (event.detail.direction === 'right' && onClickLeftArrow) {
			onClickLeftArrow();
		}
	};
</script>

<button
	aria-label="close"
	class="fixed left-0 top-0 right-0 bottom-0 z-50 bg-gray-900 opacity-60"
	onclick={onClose}
></button>

<div
	use:swipe={() => ({ timeframe: 300, minSwipeDistance: 60 })}
	onswipe={handleSwipe}
	role="dialog"
	class="xs:top-[1rem] xs:left-[1rem] xs:right-[1rem] xs:bottom-[1rem] top-[0] left-[0] right-[0] bottom-[0] bg-gray-0 fixed z-50"
>
	<div class="relative flex h-full w-full items-center">
		<div
			class="xs:gap-5 xs:translate-y-[0px] flex w-full translate-y-[-1rem] items-center justify-between px-2 sm:px-5"
		>
			{#if onClickLeftArrow}
				<Button
					variant="secondary"
					onclick={onClickLeftArrow}
					dataCy={`${dataCyPrefix}-button-left`}
					size="sm"
					class={twMerge(
						'xs:w-[auto] xs:border w-0 overflow-hidden border-0 px-0',
						classNames({
							'xs:invisible': noControls
						})
					)}
				>
					<ChevronLeftIcon />
				</Button>
			{:else}
				<Button
					variant="secondary"
					dataCy={`${dataCyPrefix}-button-left`}
					size="sm"
					class={twMerge(
						'xs:w-[auto] xs:border w-0 overflow-hidden border-0 px-0',
						classNames({
							'xs:invisible': noControls
						})
					)}
				>
					<ChevronLeftIcon />
				</Button>
			{/if}

			{#if selectedItem?.type === GalleryMedia.video}
				<div class="relative z-10 h-full w-full">
					<div data-cy={`${dataCyPrefix}-main-video`} class="relative z-10 mb-2 h-full w-full">
						<VideoPlayer
							class="max-h-[75vh] xs:max-h-[80vh] w-full"
							src={selectedItem.media.url}
							title={selectedItem.title}
							autoplay={false}
							loop={false}
							controls={true}
							playsinline={false}
							dataCy={selectedItem.title}
							style={`aspect-ratio: ${selectedItem.media.width} / ${selectedItem.media.height}`}
						/>
					</div>
				</div>
			{:else}
				<div class="relative z-10 w-full">
					<!-- <img
						data-cy={`${dataCyPrefix}-image`}
						class="select-none mx-auto max-h-[80vh] pointer-events-none touch-pinch-zoom"
						src={selectedItem?.media?.url}
						alt={selectedItem?.title}
					/> -->

					<LightboxImage
						dataCy={dataCyPrefix}
						src={selectedItem?.media?.url || ''}
						alt={selectedItem?.title || ''}
					/>
				</div>
			{/if}

			{#if onClickRightArrow}
				<Button
					variant="secondary"
					onclick={onClickRightArrow}
					dataCy={`${dataCyPrefix}-button-right`}
					size="sm"
					class={twMerge(
						'xs:w-[auto] xs:border w-0 overflow-hidden border-0 px-0',
						classNames({
							'xs:invisible': noControls
						})
					)}
				>
					<ChevronRightIcon />
				</Button>
			{:else}
				<Button
					variant="secondary"
					dataCy={`${dataCyPrefix}-button-right`}
					size="sm"
					class={twMerge(
						'xs:w-[auto] xs:border w-0 overflow-hidden border-0 px-0',
						classNames({
							'xs:invisible': noControls
						})
					)}
				>
					<ChevronRightIcon />
				</Button>{/if}
		</div>

		<div
			class="xs:bottom-[1.5rem] xs:justify-center absolute bottom-[1rem] left-0 right-0 flex items-center justify-between gap-4 px-4"
		>
			{#if onClickLeftArrow}
				<Button
					variant="secondary"
					onclick={onClickLeftArrow}
					dataCy={`${dataCyPrefix}-button-left`}
					size="sm"
					class={twMerge(
						'xs:hidden px-0',
						classNames({
							invisible: noControls
						})
					)}
				>
					<ChevronLeftIcon />
				</Button>
			{:else}
				<Button
					variant="secondary"
					dataCy={`${dataCyPrefix}-button-left`}
					size="sm"
					class={twMerge(
						'xs:hidden px-0',
						classNames({
							invisible: noControls
						})
					)}
				>
					<ChevronLeftIcon />
				</Button>
			{/if}

			<Dots
				dataCy={dataCyPrefix}
				current={selectedItemIndex}
				dots={itemsLength}
				{onDotClick}
				class={classNames({
					invisible: noControls
				})}
			/>

			{#if onClickRightArrow}
				<Button
					variant="secondary"
					onclick={onClickRightArrow}
					dataCy={`${dataCyPrefix}-button-right`}
					size="sm"
					class={twMerge(
						'xs:hidden px-0',
						classNames({
							invisible: noControls
						})
					)}
				>
					<ChevronRightIcon />
				</Button>
			{:else}
				<Button
					variant="secondary"
					dataCy={`${dataCyPrefix}-button-right`}
					size="sm"
					class={twMerge(
						'xs:hidden px-0',
						classNames({
							invisible: noControls
						})
					)}
				>
					<ChevronRightIcon />
				</Button>
			{/if}
		</div>

		<button
			data-cy={`${dataCyPrefix}-close-button`}
			class="absolute right-0 top-0 m-4"
			onclick={onClose}
		>
			<CrossIcon />
		</button>
	</div>
</div>
